import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import googleCalendarPlugin from "@fullcalendar/google-calendar";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Select,
    MenuItem,
    IconButton,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TodayIcon from "@mui/icons-material/Today";
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

function renderDescription(description) {
    if (!description) return null;

    const document = new DOMParser().parseFromString(description, "text/html");
    const renderNode = (node, key) => {
        if (node.nodeType === 3) return node.textContent;
        if (node.nodeType !== 1) return null;

        const tag = node.tagName.toLowerCase();
        if (["script", "style", "iframe", "object", "embed", "svg", "math", "template"].includes(tag)) {
            return null;
        }

        const children = Array.from(node.childNodes, (child, index) => renderNode(child, index));
        switch (tag) {
            case "b":
            case "strong":
                return <strong key={key}>{children}</strong>;
            case "i":
            case "em":
                return <em key={key}>{children}</em>;
            case "u":
                return <u key={key}>{children}</u>;
            case "br":
                return <br key={key} />;
            case "p":
            case "div":
                return <span key={key} style={{ display: "block" }}>{children}</span>;
            default:
                return <React.Fragment key={key}>{children}</React.Fragment>;
        }
    };

    return Array.from(document.body.childNodes, (node, index) => renderNode(node, index));
}

export default function RotationScheduleCalendar() {
    const theme = useTheme();
    const calendarRef = useRef(null);

    const [openPicker, setOpenPicker] = useState(false);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [currentTitle, setCurrentTitle] = useState("");

    // Helper: returns true if event is all-day
    const isAllDayEvent = (event) => {
        if (event.allDay !== undefined) return event.allDay;
        if (event.start && typeof event.start === "string" && !event.start.includes("T")) return true;
        return false;
    }

    // Properly format event date range
    const displayEventDate = (event) => {
        if (!event?.start) return "";
        const options = { year: "numeric", month: "long", day: "numeric" };
        if (isAllDayEvent(event)) {
            const start = new Date(event.start);
            const end = event.end ? new Date(event.end) : null;
            if (end) end.setDate(end.getDate() - 1);
            if (end) {
                return `${start.toLocaleDateString(undefined, options)} - ${end.toLocaleDateString(undefined, options)}`;
            } else {
                return start.toLocaleDateString(undefined, options);
            }
        } else {
            const start = new Date(event.start);
            const end = event.end ? new Date(event.end) : null;
            if (end) {
                return `${start.toLocaleDateString(undefined, options)} - ${end.toLocaleDateString(undefined, options)}`;
            } else {
                return start.toLocaleDateString(undefined, options);
            }
        }
    }

    const updateTitle = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
            const date = calendarApi.getDate();
            setCurrentTitle(date.toLocaleString("default", { month: "long", year: "numeric" }));
        }
    };

    useEffect(() => { updateTitle(); }, []);

    const gotoSelectedDate = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
            calendarApi.gotoDate(new Date(selectedYear, selectedMonth, 1));
            updateTitle();
        }
        setOpenPicker(false);
    };

    const handleEventClick = useCallback((info) => {
        info.jsEvent.preventDefault();
        setSelectedEvent({
            title: info.event.title,
            start: info.event.start, // always Date obj
            end: info.event.end,     // always Date obj
            allDay: info.event.allDay, // direct from FullCalendar
            description: info.event.extendedProps.description || "",
        });
    }, []);

    const handlePrev = () => { calendarRef.current?.getApi().prev(); updateTitle(); };
    const handleNext = () => { calendarRef.current?.getApi().next(); updateTitle(); };
    const handleToday = () => { calendarRef.current?.getApi().today(); updateTitle(); };

    return (
        <>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center",
                    bgcolor: theme.palette.background.paper,
                    py: 1,
                    px: 1,
                    mb: 0,
                    color: theme.palette.accent.main,
                    fontWeight: 600,
                    border: "1px solid var(--fc-border-color)",
                    borderBottom: "none"
                }}
            >
                {/* Left */}
                <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton onClick={handlePrev} color="inherit"><ArrowLeftIcon /></IconButton>
                    <IconButton onClick={handleToday} color="inherit">
                        <TodayIcon />
                    </IconButton>
                </Box>

                {/* Center */}
                <Box sx={{ textAlign: "center" }}>
                    <Button onClick={() => setOpenPicker(true)} color="inherit">
                        {currentTitle}
                    </Button>
                </Box>

                {/* Right */}
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <IconButton onClick={handleNext} color="inherit"><ArrowRightIcon /></IconButton>
                </Box>
            </Box>

            {/* Calendar */}
            <Box
                sx={{
                    "& .fc .fc-col-header": {
                        bgcolor: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                    }, "& .fc-event, & .fc-h-event & .fc-daygrid-block-event": {
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        border: "1px solid var(--fc-border-color)",
                    },
                    "& .fc-event-title": {
                        color: theme.palette.text.primary,
                        border: 'none'
                    },
                    "& .fc": { border: "none", borderTop: "none", px: 0, py: 0 },
                    ".fc-event:active, .fc-h-event:active, .fc-daygrid-event:active": {
                        opacity: '1 !important',
                        transform: 'none !important'
                    },
                    "& .fc-event-time": {
                        color: theme.palette.secondary.main,
                    },
                }}
            >
                {useMemo(() => (
                    <FullCalendar
                        ref={calendarRef}
                        plugins={[dayGridPlugin, interactionPlugin, googleCalendarPlugin]}
                        initialView="dayGridMonth"
                        googleCalendarApiKey={process.env.REACT_APP_GOOGLE_CALENDAR_API_KEY}
                        events={{
                            googleCalendarId: process.env.REACT_APP_GOOGLE_CALENDAR_ID,
                        }}
                        headerToolbar={false}
                        height="auto"
                        eventClick={handleEventClick}
                        eventDataTransform={(eventData) => {
                            const { url, ...rest } = eventData;

                            const title = eventData.title || '';
                            if (title.startsWith('SALE:')) {
                                rest.backgroundColor = theme.palette.calendar.secondary;
                                rest.borderColor = theme.palette.divider;
                            } else if (title.startsWith('POOL:')) {
                                rest.backgroundColor = theme.palette.calendar.main;
                                rest.borderColor = theme.palette.divider;
                            } else {
                                rest.backgroundColor = theme.palette.calendar.default;
                                rest.borderColor = theme.palette.divider;
                            }
                            return rest;
                        }}
                    />
                ), [theme, handleEventClick])}
            </Box>

            {/* Event Dialog */}
            <Dialog
                open={!!selectedEvent}
                onClose={() => setSelectedEvent(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { bgcolor: theme.palette.background.default } }}
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
                    {selectedEvent?.title}
                    <IconButton onClick={() => setSelectedEvent(null)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        <strong>Date:</strong> {displayEventDate(selectedEvent)}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                    >
                        {renderDescription(selectedEvent?.description)}
                    </Typography>
                </DialogContent>
            </Dialog>

            {/* Month/Year Picker Dialog */}
            <Dialog
                open={openPicker}
                onClose={() => setOpenPicker(false)}
                PaperProps={{ sx: { bgcolor: theme.palette.background.default } }}
            >
                <DialogTitle>Select Month & Year</DialogTitle>
                <DialogContent sx={{ display: "flex", gap: 2, pt: 1 }}>
                    <Select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        sx={{ bgcolor: theme.palette.background.default }}
                        MenuProps={{
                            PaperProps: {
                                sx: { bgcolor: theme.palette.background.default },
                            },
                        }}
                    >
                        {Array.from({ length: 12 }, (_, i) => (
                            <MenuItem key={i} value={i}>
                                {new Date(0, i).toLocaleString("default", { month: "long" })}
                            </MenuItem>
                        ))}
                    </Select>

                    <Select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                        sx={{ bgcolor: theme.palette.background.default }}
                        MenuProps={{
                            PaperProps: {
                                sx: { bgcolor: theme.palette.background.default },
                            },
                        }}
                    >
                        {Array.from({ length: 20 }, (_, i) => {
                            const year = new Date().getFullYear() - 10 + i;
                            return <MenuItem key={year} value={year}>{year}</MenuItem>;
                        })}
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPicker(false)}>Cancel</Button>
                    <Button variant="contained" onClick={gotoSelectedDate}>
                        Go
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
